/**
 * APP Plus Configuration (P7.1c) — the §6.10 page-level tab bar (light slate
 * chips, solid-navy active) with the D5-locked tab set: ACO Settings · CAHPS
 * Entry · Claims Measures · IA · Roster ⌜P8⌟ · PI Reporting ⌜P9⌟ (future tabs
 * visible, chip-marked, disabled).
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { useAppPlusScoring } from './lib/useAppPlusScoring';
import { AcoHeader, NoAcoState, YearUnsupportedState } from './components/AcoHeader';
import { AcoSettingsTab } from './configuration/AcoSettingsTab';
import { CahpsEntryTab } from './configuration/CahpsEntryTab';
import { ClaimsMeasuresTab } from './configuration/ClaimsMeasuresTab';
import { CmsReportsTab } from './configuration/CmsReportsTab';

// §6.10 trigger classes (page-level tab bar recipe)
const TRIGGER =
  'px-1.5 2xl:px-4 py-3 text-xs 2xl:text-sm font-medium bg-slate-100 text-slate-700 rounded-lg transition-all ' +
  'data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm ' +
  'hover:bg-slate-200 data-[state=active]:hover:bg-primary justify-center';

const TAB_VALUES = ['cms-reports', 'settings', 'cahps', 'claims'] as const;

export default function AppPlusConfiguration() {
  const { selectedAco, loading: acoLoading } = useAco();
  const { programYear } = useProgramYear();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);
  // Deep-linkable tabs (owner 2026-07-20): QPS "Enter in Configuration" links
  // land on the right tab via ?tab= (e.g. ?tab=claims for 479/484).
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState<string>(
    requested && (TAB_VALUES as readonly string[]).includes(requested) ? requested : 'settings');
  useEffect(() => {
    if (requested && (TAB_VALUES as readonly string[]).includes(requested)) setTab(requested);
  }, [requested]);

  if (!scoring.yearSupported) return <YearUnsupportedState year={programYear} />;
  if (acoLoading || scoring.loading) {
    // Shell-first (owner 2026-07-20): page chrome renders immediately.
    return (
      <div>
        <AcoHeader title="Configuration" subtitle="ACO settings and data entry" />
        <Skeleton className="h-11 w-full rounded-lg" />
        <Skeleton className="mt-6 h-96 w-full rounded-lg" />
      </div>
    );
  }
  if (!selectedAco) return <NoAcoState />;

  return (
    <div>
      <AcoHeader title="Configuration" subtitle="ACO settings and data entry" />
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        {/* Roster tab removed (owner 2026-07-15): the participant file uploads in
            CMS Reports (it IS a CMS export); roster analytics live on Insights.
            IA tab PRESENT BUT DISABLED like PI (owner 2026-07-20: not deleted —
            kept until the auto-credit rule is beyond doubt). */}
        <TabsList className="grid w-full grid-cols-6 gap-1 2xl:gap-2 bg-transparent p-0 rounded-none h-auto">
          {/* CMS Reports first — mirrors MIPS Configuration's QPP Reports position */}
          <TabsTrigger value="cms-reports" className={TRIGGER}>
            <span className="hidden 2xl:inline">CMS Reports</span><span className="2xl:hidden">CMS</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className={TRIGGER}>
            <span className="hidden 2xl:inline">ACO Settings</span><span className="2xl:hidden">Settings</span>
          </TabsTrigger>
          <TabsTrigger value="cahps" className={TRIGGER}>
            <span className="hidden 2xl:inline">CAHPS Entry</span><span className="2xl:hidden">CAHPS</span>
          </TabsTrigger>
          <TabsTrigger value="claims" className={TRIGGER}>
            <span className="hidden 2xl:inline">Claims Measures</span><span className="2xl:hidden">Claims</span>
          </TabsTrigger>
          <TabsTrigger value="ia" className={TRIGGER} disabled>
            IA
            <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">Disabled</Badge>
          </TabsTrigger>
          <TabsTrigger value="pi" className={TRIGGER} disabled>
            <span className="hidden 2xl:inline">PI Reporting</span><span className="2xl:hidden">PI</span>
            <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">Disabled</Badge>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="cms-reports" className="space-y-4">
          <CmsReportsTab />
        </TabsContent>
        <TabsContent value="settings" className="space-y-4">
          <AcoSettingsTab acoId={selectedAco.id} scoring={scoring} />
        </TabsContent>
        <TabsContent value="cahps" className="space-y-4">
          <CahpsEntryTab scoring={scoring} />
        </TabsContent>
        <TabsContent value="claims" className="space-y-4">
          <ClaimsMeasuresTab scoring={scoring} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
